import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import { listAuditEvents, getAuditExport } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';

const PER_PAGE = 15;

/**
 * Global Audit data (CT-4210/4211). provider / instance / actor / code sync to
 * the URL (deep-linkable + bookmarkable, CT-4213); free-text search + date +
 * severity stay in component state (no per-keystroke URL churn). `applyFilters`
 * commits the staged "All filters" slideout draft in one shot. All filters
 * default to empty (nothing preselected on load).
 */
export function useGlobalAudit() {
  const { t } = useTranslation('admin-studio');
  const [searchParams, setSearchParams] = useSearchParams();

  const provider = searchParams.get('provider') || undefined;
  const instance = searchParams.get('instance') || undefined;
  const actor = searchParams.get('actor') || undefined;
  const code = searchParams.get('code') || undefined;

  const [search, setSearch] = useState('');
  // No filter preselected by default — the view loads unfiltered (newest first)
  // and "Clear Filters" only surfaces once the user picks a filter or searches.
  const [dateRange, setDateRange] = useState([]);
  const [severities, setSeverities] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PER_PAGE);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [now, setNow] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    if (fetchId === 1) setIsLoading(true);
    setError(null);
    try {
      const res = await listAuditEvents({
        provider,
        instance,
        actor,
        code,
        search,
        severities,
        date_range: dateRange[0] || undefined,
        page,
        page_size: perPage,
      });
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      const normalized = validateApiResponse(res, t('common.requestFailed'));
      setItems(normalized.body ?? []);
      setTotal(Number(normalized.meta?.total ?? 0));
      setNow(Date.now());
    } catch (err) {
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setError(err?.message ?? t('common.requestFailed'));
    } finally {
      if (mountedRef.current && fetchId === fetchIdRef.current) setIsLoading(false);
    }
  }, [provider, instance, actor, code, search, severities, dateRange, page, perPage, t]);

  useEffect(() => {
    load();
  }, [load]);

  // provider / instance / actor / event-code live in the URL (deep-link +
  // bookmarkable, CT-4213); writing them keeps the view shareable. Free-text
  // search + date + severity stay in component state (no per-keystroke churn).
  const setParam = useCallback(
    (key, value) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      });
      setPage(1);
    },
    [setSearchParams],
  );
  const setProvider = useCallback(
    (value) => {
      // Changing the provider drops any instance scoped to the previous one.
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set('provider', value);
        else next.delete('provider');
        next.delete('instance');
        return next;
      });
      setPage(1);
    },
    [setSearchParams],
  );
  const setInstance = useCallback((value) => setParam('instance', value), [setParam]);

  const clearFilters = useCallback(() => {
    setSearch('');
    setDateRange([]);
    setSeverities([]);
    setPage(1);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      ['provider', 'instance', 'actor', 'code'].forEach((k) => next.delete(k));
      return next;
    });
  }, [setSearchParams]);

  const updateSearch = useCallback((next) => {
    setSearch(next);
    setPage(1);
  }, []);
  const updateDateRange = useCallback((next) => {
    setDateRange(next);
    setPage(1);
  }, []);
  const updateSeverities = useCallback((next) => {
    setSeverities(next);
    setPage(1);
  }, []);
  const updatePerPage = useCallback((size) => {
    setPerPage(size);
    setPage(1);
  }, []);

  // Atomic commit for the "All filters" slideout (staged apply): the drawer edits
  // a local draft and commits everything here at once when "Apply" is clicked.
  const applyFilters = useCallback(
    (draft) => {
      setDateRange(draft.dateRange ? [draft.dateRange] : []);
      setSeverities(draft.severities ?? []);
      setPage(1);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        ['provider', 'instance', 'actor', 'code'].forEach((k) => {
          if (draft[k]) next.set(k, draft[k]);
          else next.delete(k);
        });
        return next;
      });
    },
    [setSearchParams],
  );

  // Fetch every row matching the active filters (all pages) for CSV export — the
  // download must cover the full filtered set, not just the visible page (CT-4212).
  const exportRows = useCallback(async () => {
    const res = await getAuditExport({
      provider,
      instance,
      actor,
      code,
      search,
      severities,
      date_range: dateRange[0] || undefined,
    });
    return validateApiResponse(res, t('common.requestFailed')).body ?? [];
  }, [provider, instance, actor, code, search, severities, dateRange, t]);

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return useMemo(
    () => ({
      items,
      total,
      totalPages,
      page,
      perPage,
      now,
      isLoading,
      error,
      filters: { provider, instance, actor, code, search, severities, dateRange },
      setSearch: updateSearch,
      setDateRange: updateDateRange,
      setSeverities: updateSeverities,
      setProvider,
      setInstance,
      applyFilters,
      setPage,
      setPerPage: updatePerPage,
      clearFilters,
      exportRows,
    }),
    [
      items,
      total,
      totalPages,
      page,
      perPage,
      now,
      isLoading,
      error,
      provider,
      instance,
      actor,
      code,
      search,
      severities,
      dateRange,
      updateSearch,
      updateDateRange,
      updateSeverities,
      setProvider,
      setInstance,
      applyFilters,
      updatePerPage,
      clearFilters,
      exportRows,
    ],
  );
}
